import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, switchMap, map, tap, catchError, of, shareReplay } from 'rxjs';
import { DeveloperCatalogService } from '@6grain/masofa-monolith-api-client/common';
import {
  CatalogCommand,
  CatalogDto,
  CatalogJob,
  normalizeDeveloperCatalog,
} from '@app/pages/admin/developer-catalog-page/developer-catalog.utils';

@Injectable({ providedIn: 'root' })
export class DeveloperCatalogStore {
  private readonly api = inject(DeveloperCatalogService);
  private readonly reload$ = new BehaviorSubject<void>(undefined);
  private readonly loadingSubject = new BehaviorSubject<boolean>(true);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);

  readonly loading$ = this.loadingSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();

  readonly catalog$: Observable<CatalogDto | null> = this.reload$.pipe(
    tap(() => {
      this.loadingSubject.next(true);
      this.errorSubject.next(null);
    }),
    switchMap(() =>
      this.api.developerCatalogGet().pipe(
        map((catalog) => normalizeDeveloperCatalog(catalog)),
        catchError((err) => {
          console.error(err);
          this.errorSubject.next('Failed to load code catalog');
          return of(null);
        }),
      ),
    ),
    tap(() => this.loadingSubject.next(false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  reload(): void {
    this.reload$.next();
  }

  findCommand(name: string): Observable<CatalogCommand | null> {
    return this.catalog$.pipe(
      map((catalog) => catalog?.commands?.find((command) => command.name === name) ?? null),
    );
  }

  findJob(name: string): Observable<CatalogJob | null> {
    return this.catalog$.pipe(
      map((catalog) => catalog?.jobs?.find((job) => job.name === name) ?? null),
    );
  }
}
